<template>
  <v-container fluid fill-height :class="{ 'small-device': isSmallDevice }">
    <!-- Hidden Scanner Input -->
    <input
      ref="scannerInput"
      type="text"
      inputmode="none"
      :class="scannerOptions.debug ? 'scanner-input-visible' : 'scanner-input-hidden'"
      @input="handleScannerInput"
      @keydown="handleScannerKeydown"
      @focus="handleScannerFocus"
      @blur="handleScannerBlur"
      autocomplete="off"
    />

    <!-- Welcome Message Overlay -->
    <transition name="fade">
      <div v-if="showWelcomeMessage" class="welcome-overlay">
        <div class="welcome-content">
          <v-icon size="100" color="success" class="mb-4 pulse-icon">mdi-check-circle</v-icon>
          <h1 class="welcome-title">Hallo {{ welcomeUserName }}!</h1>
        </div>
      </div>
    </transition>

    <!-- Small Device Variant - Scanner Optimized -->
    <div v-if="isSmallDevice" class="scanner-login-container">

      <!-- Scanning State -->
      <div v-if="!isLoading && !error && !showWelcomeMessage" class="scan-idle-state">
        <div class="scan-icon-wrapper">
          <v-icon :size="120" color="primary" class="pulse-icon">mdi-key</v-icon>
        </div>

        <!-- Main Instruction -->
        <h2 class="scan-title">Scanne deinen Schlüssel zum einloggen</h2>

      </div>

      <!-- Loading State -->
      <div v-if="isLoading && !showWelcomeMessage" class="scan-loading-state">
        <v-progress-circular indeterminate color="primary" size="80"></v-progress-circular>
        <h3 class="mt-6">Anmeldung läuft...</h3>
      </div>

      <!-- Error State -->
      <div v-if="error && !isLoading && !showWelcomeMessage" class="scan-error-state">
        <v-icon size="80" color="error" class="shake-icon">mdi-alert-circle</v-icon>
        <h3 class="error-title mt-4">Anmeldung fehlgeschlagen</h3>
        <p class="error-message">{{ error }}</p>
        <v-btn color="primary" @click="resetError" class="mt-4">
          <v-icon left>mdi-restart</v-icon>
          Erneut versuchen
        </v-btn>
      </div>

      <!-- Manual Login Toggle (Small) -->
      <div class="manual-login-toggle">
        <v-btn text small color="grey" @click="showManualLogin = !showManualLogin">
          <v-icon left small>mdi-keyboard</v-icon>
          Manuelle Anmeldung
        </v-btn>
      </div>

      <!-- Manual Login Dialog -->
      <v-dialog v-model="showManualLogin" max-width="400">
        <v-card>
          <v-card-title>
            Manuelle Anmeldung
            <v-spacer></v-spacer>
            <v-btn icon @click="showManualLogin = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-form ref="loginForm" @submit.prevent="fireLogin">
              <v-text-field
                v-model="login.user"
                name="username"
                label="Benutzername"
                prepend-inner-icon="person"
                outlined
                dense
                required
                :rules="[rules.required]"
                autocomplete="username"
                class="mb-2"
                @keydown.enter="fireLogin"
              ></v-text-field>

              <v-text-field
                v-model="login.password"
                label="Passwort"
                name="password"
                type="password"
                prepend-inner-icon="lock"
                outlined
                dense
                required
                :rules="[rules.required]"
                autocomplete="current-password"
                @keydown.enter="fireLogin"
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-btn
              block
              large
              color="primary"
              :loading="isLoading"
              :disabled="!isFormValid"
              @click="fireLogin"
            >
              <v-icon left>mdi-login</v-icon>
              Anmelden
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <!-- Desktop/Tablet Variant - Standard Form -->
    <v-layout v-else align-center justify-center row>
      <v-flex xs12 sm8 md6 lg4>
        <!-- Development Server Warning -->
        <v-alert
          v-if="isDevServer"
          type="warning"
          prominent
          border="left"
          class="mb-4"
        >
          <v-row align="center">
            <v-col class="grow">
              <strong>⚠️ Entwicklungsumgebung</strong>
              <div class="mt-1">Sie befinden sich auf dem Entwicklungsserver. Änderungen können die Produktion beeinflussen!</div>
            </v-col>
          </v-row>
        </v-alert>

        <v-card elevation="8" class="rounded-lg">
          <!-- Logo/Header Section -->
          <v-card-title class="justify-center pt-8 pb-4">
            <div class="text-center">
              <h1 class="text-h4 font-weight-bold mb-2">Anmeldung</h1>
              <p class="text-subtitle-1 grey--text">Bitte melden Sie sich mit Ihren Zugangsdaten an</p>
            </div>
          </v-card-title>

          <!-- Error Alert -->
          <v-alert
            v-if="error"
            type="error"
            dismissible
            @input="error = null"
            class="mx-4"
          >
            {{ error }}
          </v-alert>

          <v-card-text class="px-8 pb-4">
            <v-form ref="loginForm" @submit.prevent="fireLogin">
              <v-text-field
                v-model="login.user"
                name="username"
                label="Benutzername"
                prepend-inner-icon="person"
                outlined
                dense
                required
                :rules="[rules.required]"
                autocomplete="username"
                class="mb-2"
                @keydown.enter="fireLogin"
              ></v-text-field>

              <v-text-field
                v-model="login.password"
                label="Passwort"
                name="password"
                type="password"
                prepend-inner-icon="lock"
                outlined
                dense
                required
                :rules="[rules.required]"
                autocomplete="current-password"
                @keydown.enter="fireLogin"
              ></v-text-field>
            </v-form>
          </v-card-text>

          <v-card-actions class="px-8 pb-8">
            <v-btn
              block
              x-large
              color="primary"
              :loading="isLoading"
              :disabled="!isFormValid"
              @click="fireLogin"
              elevation="2"
            >
              <v-icon left>mdi-login</v-icon>
              Anmelden
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Server Info -->
        <v-card v-if="isDevServer" flat class="mt-4 transparent">
          <v-card-text class="text-center text-caption grey--text">
            Entwicklungsserver • Nur für Testzwecke verwenden
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import AuthService from "@/services/AuthService";
import router from "../router";
import EventBus from '@/event-bus';
import scannerInputMixin from '@/mixins/scannerInputMixin';
import '@/styles/scanner-input.css';

export default {
  runtimeCompiler: true,
  mixins: [scannerInputMixin],
  components: {},
  data: () => ({
    login: {
      user: null,
      password: null,
      key: null
    },
    error: null,
    isLoading: false,
    rules: {
      required: value => !!value || 'Dieses Feld ist erforderlich'
    },
    // UI State
    showManualLogin: false,
    windowWidth: window.innerWidth,
    // Welcome message
    showWelcomeMessage: false,
    welcomeUserName: '',
    // Scanner options (from mixin)
    scannerOptions: {
      enabled: true,
      refocusDelay: 3000,
      debug: false
    }
  }),
  computed: {
    isDevServer() {
      return window.location.hostname.includes('dev') ||
             window.location.hostname.includes('localhost') ||
             window.location.port === '8080';
    },
    isFormValid() {
      // Valid if we have user + password OR user + key
      return this.login.user && (this.login.password || this.login.key);
    },
    isSmallDevice() {
      // Check for query parameter override first
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('mobile') === 'true' || urlParams.get('mobile') === '1') {
        return true;
      }

      return this.windowWidth <= 480;
    }
  },
  methods: {
    /**
     * Handle scan from DataWedge (via scannerInputMixin)
     * This method is called by the mixin when a scan is received
     */
    onScanReceived(scannedValue) {
      console.log('🔍 [LoginForm] Processing scan from DataWedge:', scannedValue);
      console.log('📏 [LoginForm] Length:', scannedValue.length);

      // Set login credentials for RFID authentication
      this.login.user = 'rfid_login';
      this.login.password = 'rfid_key_auth';
      this.login.key = scannedValue;

      // Auto-login with scanned key
      this.fireLogin();
    },

    async fireLogin() {
      // For scanner login, skip form validation - let backend decide
      const isScannerLogin = this.login.key && this.login.user === 'rfid_login';

      if (!isScannerLogin && !this.isFormValid) {
        console.log('❌ Manual login form is not valid');
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        console.log('📡 Sending authentication request to backend...');
        await AuthService.login(this.login);
        EventBus.$emit('user-logged-in');
        localStorage.removeItem("lastTimestamp");

        // Get user info to display name in welcome message
        const userResponse = await AuthService.getUser();
        const userName = userResponse.data.user;

        // Redirect based on device type and login method
        if (this.isSmallDevice && isScannerLogin) {
          // Small device + scanner login -> show welcome message for 4 seconds
          this.welcomeUserName = userName;
          this.showWelcomeMessage = true;

          // Hide welcome message and redirect after 4 seconds
          setTimeout(() => {
            this.showWelcomeMessage = false;
            router.push("/warehouse/task-choice");
          }, 4000);
        } else if (this.isSmallDevice) {
          // Small device + manual login -> redirect to task choice immediately
          router.push("/warehouse/task-choice");
        } else {
          // Desktop login -> redirect to dashboard
          router.push("/");
        }
      } catch (ex) {
        const errorMessage = ex?.response?.data?.[2]?.message ||
                            ex?.response?.data?.message ||
                            'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.';
        this.error = errorMessage;

        // Auto-dismiss error after 5 seconds
        setTimeout(() => {
          this.error = null;
        }, 5000);
      } finally {
        this.isLoading = false;

        // Reset login fields after attempt
        if (!isScannerLogin) {
          this.login.key = null;
        }
      }
    },

    resetError() {
      this.error = null;
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    }
  },
  created() {
    console.log('[LoginForm] Window width:', this.windowWidth);
    console.log('[LoginForm] isSmallDevice:', this.isSmallDevice);

    // Enable scanner debug mode on small devices or with URL param
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true' || this.isSmallDevice) {
      this.scannerOptions.debug = false;
      console.log('🐛 [LoginForm] Scanner debug mode ENABLED');
    }

    // Add resize listener
    window.addEventListener('resize', this.handleResize);
  },
  async mounted() {
    // Force logout when accessing login page
    // This ensures user is truly logged out and not connected to socket namespaces
    try {
      console.log('🔓 [Login] Forcing logout on login page mount');
      await AuthService.logout();
      EventBus.$emit('user-logged-out');
    } catch (error) {
      console.log('🔓 [Login] Logout failed (probably already logged out):', error.message);
    }
  },
  beforeDestroy() {
    // Remove resize listener
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
.v-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.rounded-lg {
  border-radius: 12px !important;
}

.v-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

.v-text-field {
  margin-bottom: 8px;
}

/* Welcome Message Overlay */
.welcome-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.welcome-content {
  text-align: center;
  padding: 20px;
}

.welcome-title {
  font-size: 48px;
  font-weight: 700;
  color: #333;
  margin: 0;
  line-height: 1.2;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Small Device Styles */
.v-container.small-device {
  padding: 0 !important;
  background: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.scanner-login-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  overflow: hidden;
  touch-action: none;
}

/* Scan Idle State */
.scan-idle-state {
  text-align: center;
  color: #333;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.scan-icon-wrapper {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.scan-title {
  font-size: 28px;
  font-weight: 600;
  color: #333 !important;
  margin: 0;
  width: 100%;
  text-align: center;
  line-height: 1.4;
}

.scanning-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
  color: #333;
  font-size: 18px;
  font-weight: 500;
  background: #f5f5f5;
  padding: 16px 24px;
  border-radius: 12px;
}

.scan-debug-buffer {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
  font-family: monospace;
}

/* Scan Loading State */
.scan-loading-state {
  text-align: center;
  color: #333;
}

.scan-loading-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

/* Scan Error State */
.scan-error-state {
  text-align: center;
  color: #333;
  max-width: 320px;
}

.shake-icon {
  animation: shake 0.5s;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

.error-title {
  font-size: 24px;
  font-weight: 700;
  color: #f44336;
}

.error-message {
  font-size: 18px;
  color: #333;
  margin-top: 12px;
  line-height: 1.4;
}

/* Manual Login Toggle */
.manual-login-toggle {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
}

/* Responsive Adjustments */
@media (max-width: 480px) {
  .welcome-title {
    font-size: 36px;
  }

  .scan-title {
    font-size: 22px;
  }

  .scanner-login-container {
    padding: 16px;
  }

  .logo-wrapper {
    margin-bottom: 20px;
  }

  .company-logo {
    max-width: 160px;
  }

  .scan-icon-wrapper {
    margin-bottom: 20px;
  }

  .scanning-indicator {
    font-size: 16px;
    padding: 12px 20px;
    margin-top: 32px;
  }

  .error-title {
    font-size: 22px;
  }

  .error-message {
    font-size: 16px;
  }

  .scan-loading-state h3 {
    font-size: 22px;
  }
}
</style>